import { Button, DatePicker, EmptyState, ErrorState, Icon, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarPlus, TbCalendarTime } from "react-icons/tb";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { getBrowserTimezone } from "@/features/schedule/utils/schedule-timezone.utils";
import {
    projectBookingCountsFromStorage,
    projectSlotsFromStorage,
} from "@/features/schedule/utils/slot-timezone.utils";
import { TimezoneSelect } from "@/pages/management/schedules/components/schedule-edit-views";
import { useBookings } from "@/queries/booking.queries";
import { useEventType } from "@/queries/event-type.queries";
import { useEventTypeSlots } from "@/queries/slot.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { useBookableSlotsDateRange } from "../slots/hooks/use-bookable-slots-date-range";
import { BookAppointmentSheet } from "./components/book-appointment-sheet";
import { BookingSlotsGrid } from "./components/booking-slots-grid";

const BookingsSearchSchema = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.EVENT_BOOKINGS)({
    component: EventBookingsPage,
    validateSearch: searchValidator(BookingsSearchSchema),
});

function EventBookingsPage() {
    const { t } = useTranslation();
    const { id: eventTypeId } = Route.useParams();
    const search = Route.useSearch({ structuralSharing: true });
    const navigate = Route.useNavigate();

    const [bookSheetOpen, setBookSheetOpen] = useState(false);
    const [displayTimezone, setDisplayTimezone] = useState(getBrowserTimezone);

    const { data: eventType, isPending: isEventTypePending } = useEventType(eventTypeId);

    const updateSearch = useCallback(
        (updates: { startDate?: string; endDate?: string }) => {
            navigate({
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    const { startDate, endDate, startDateStr, endDateStr, datesInRange, handleStartDateChange, handleEndDateChange } =
        useBookableSlotsDateRange({
            search,
            onSearchChange: updateSearch,
        });

    const {
        data: slots,
        isPending: isSlotsPending,
        isError: isSlotsError,
        error: slotsError,
        refetch: refetchSlots,
    } = useEventTypeSlots(eventTypeId, startDateStr, endDateStr, { includeUnavailable: true });

    const {
        data: bookings,
        isPending: isBookingsPending,
        isError: isBookingsError,
        error: bookingsError,
        refetch: refetchBookings,
    } = useBookings();

    const displaySlots = useMemo(() => projectSlotsFromStorage(slots ?? {}, displayTimezone), [slots, displayTimezone]);

    const eventBookings = useMemo(
        () => bookings?.filter((booking) => booking.eventTypeId === eventTypeId) ?? [],
        [bookings, eventTypeId]
    );

    const bookingCounts = useMemo(() => {
        const counts: Record<string, Record<string, number>> = {};

        for (const booking of eventBookings) {
            if (!counts[booking.date]) counts[booking.date] = {};
            counts[booking.date][booking.startTime] = (counts[booking.date][booking.startTime] ?? 0) + 1;
        }

        return projectBookingCountsFromStorage(counts, displayTimezone);
    }, [eventBookings, displayTimezone]);

    const eventBookingsInRangeCount = useMemo(() => {
        let count = 0;

        for (const date of datesInRange) {
            const dayCounts = bookingCounts[date];
            if (!dayCounts) continue;
            for (const slotCount of Object.values(dayCounts)) {
                count += slotCount;
            }
        }

        return count;
    }, [datesInRange, bookingCounts]);

    const maxBookingCount = useMemo(() => {
        let max = 0;

        for (const date of datesInRange) {
            for (const slot of displaySlots[date] ?? []) {
                const count = bookingCounts[date]?.[slot.startTime] ?? 0;
                if (count > max) max = count;
            }
        }

        return max;
    }, [datesInRange, displaySlots, bookingCounts]);

    const totalSlots = useMemo(
        () => datesInRange.reduce((sum, date) => sum + (displaySlots[date]?.length ?? 0), 0),
        [datesInRange, displaySlots]
    );

    const isLoading = isEventTypePending || isSlotsPending || isBookingsPending;
    const isError = isSlotsError || isBookingsError;
    const error = slotsError ?? bookingsError;

    const refetch = useCallback(() => {
        refetchSlots();
        refetchBookings();
    }, [refetchSlots, refetchBookings]);

    return (
        <div className="flex h-0 min-h-0 flex-1 flex-col gap-4">
            <AdminSurface className="flex shrink-0 flex-col gap-4 p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="size-2 rounded-full bg-emerald-500/80" aria-hidden />
                            <h2 className="text-base font-semibold tracking-tight">{t("event_bookings")}</h2>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {eventType && (
                                <>
                                    <span className="rounded-lg border border-sky-500/20 bg-sky-500/5 px-2 py-1 text-sky-800 dark:text-sky-300">
                                        {t("max_booking_days_ahead")}: {eventType.maxBookingDaysAhead}
                                    </span>
                                    <span className="rounded-lg border border-sky-500/20 bg-sky-500/5 px-2 py-1 text-sky-800 dark:text-sky-300">
                                        {t("min_booking_notice_minutes")}: {eventType.minBookingNoticeMinutes}
                                    </span>
                                    {eventType.isActive === false && (
                                        <span className="rounded-lg border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive">
                                            {t("inactive")}
                                        </span>
                                    )}
                                </>
                            )}
                            {!isLoading && (
                                <>
                                    <span className="rounded-lg border border-sky-500/25 bg-sky-500/10 px-2 py-1 text-sky-800 dark:text-sky-300">
                                        {t("bookable_slots_count", { count: totalSlots })}
                                    </span>
                                    <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-700 dark:text-emerald-400">
                                        {t("event_bookings_count", { count: eventBookingsInRangeCount })}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <TimezoneSelect
                            value={displayTimezone}
                            onChange={setDisplayTimezone}
                            className="w-full max-w-56 sm:w-56"
                        />
                        <DatePicker date={startDate.toJSDate()} onDateChange={handleStartDateChange} />
                        <span className="hidden text-muted-foreground sm:inline">–</span>
                        <DatePicker date={endDate.toJSDate()} onDateChange={handleEndDateChange} />
                    </div>
                    <PageActions>
                        <Button onClick={() => setBookSheetOpen(true)}>
                            <Icon icon={TbCalendarPlus} />
                            {t("book_appointment")}
                        </Button>
                    </PageActions>
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-border/50 pt-3 text-xs text-muted-foreground">
                    <span className="font-medium">{t("booking_heatmap_legend")}</span>
                    <div className="flex items-center gap-1.5">
                        <span className="size-3 rounded-md border border-border/50 bg-muted/30" />
                        <span>0</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="size-3 rounded-md border border-emerald-500/25 bg-emerald-500/10" />
                        <span className="size-3 rounded-md border border-emerald-500/35 bg-emerald-500/20" />
                        <span className="size-3 rounded-md border border-emerald-500/45 bg-emerald-500/30" />
                        <span className="size-3 rounded-md border border-emerald-600/50 bg-emerald-500/45" />
                    </div>
                    <span>{t("booking_heatmap_more")}</span>
                </div>
            </AdminSurface>

            {isError && (
                <ErrorState
                    title={t("failed_to_load_event_bookings")}
                    message={error?.message || t("event_load_error")}
                    onRetry={refetch}
                />
            )}

            {isLoading && (
                <div className="grid min-h-0 flex-1 auto-rows-[minmax(17.5rem,20rem)] grid-cols-2 gap-2.5 overflow-y-auto md:grid-cols-3 lg:grid-cols-4 xl:h-full xl:auto-rows-fr xl:grid-cols-7 xl:overflow-hidden">
                    {Array.from({ length: datesInRange.length || 7 }).map((_, index) => (
                        <Skeleton key={index} className="min-h-70 w-full rounded-2xl xl:h-full xl:min-h-0" />
                    ))}
                </div>
            )}

            {!isLoading && !isError && datesInRange.length === 0 && (
                <AdminSurface className="p-8">
                    <EmptyState
                        variant="simple"
                        icon={<TbCalendarTime size={40} className="text-muted-foreground" />}
                        title="no_bookable_slots_found"
                    />
                </AdminSurface>
            )}

            {!isLoading && !isError && datesInRange.length > 0 && (
                <div className="flex h-0 min-h-0 flex-1 flex-col">
                    <BookingSlotsGrid
                        datesInRange={datesInRange}
                        slots={displaySlots}
                        bookingCounts={bookingCounts}
                        maxBookingCount={maxBookingCount}
                    />
                </div>
            )}

            <BookAppointmentSheet
                eventTypeId={eventTypeId}
                open={bookSheetOpen}
                onOpenChange={setBookSheetOpen}
                onSuccess={refetch}
            />
        </div>
    );
}
